/**
 * AnexoUploadForm — escolher arquivo + descrição e enviar.
 *
 * Extraído do AnexosBlock do lead (CRM-50) para que qualquer tela que aceite
 * anexo use a MESMA validação do backend antes de subir o arquivo: passar 10 MB
 * pela rede para receber um 400 é caro e a mensagem chegava só no toast.
 *
 * Props:
 *   - onEnviar({ arquivo, descricao }) => Promise — obrigatório
 *   - accept / extensoes / maxBytes — default: o que o LeadAnexo aceita
 *   - rotulo, placeholderDescricao — textos da tela
 *   - dica — sobrescreve a frase de limites (por padrão sai de maxBytes/extensoes)
 */
import React, { useRef, useState } from "react";
import { FiAlertCircle, FiUpload } from "react-icons/fi";

import {
  ACCEPT_ANEXO_LEAD,
  EXTENSOES_ANEXO_LEAD,
  MAX_BYTES_ANEXO_LEAD,
  formatarTamanho,
  validarArquivoAnexo,
} from "../../utils/documentos";
import {
  ErrorMsg,
  Field,
  InlineHint,
  Input,
} from "../../pages/Orcamentos/styles";
import AsyncButton from "../AsyncButton";

export default function AnexoUploadForm({
  onEnviar,
  accept = ACCEPT_ANEXO_LEAD,
  extensoes = EXTENSOES_ANEXO_LEAD,
  maxBytes = MAX_BYTES_ANEXO_LEAD,
  rotulo = "Anexar novo",
  placeholderDescricao = "Ex: Planta do apartamento",
  dica = null,
  successMessage = "Anexo enviado.",
}) {
  // A dica tem que dizer o que a validação REALMENTE aplica: era texto fixo
  // ("10MB ... DWG, SKP") enquanto `maxBytes`/`extensoes` são props, então
  // quem passasse outro limite mostrava a regra errada (CRM-50).
  const dicaDosLimites =
    dica ||
    `Máx. ${formatarTamanho(maxBytes)}. Tipos: ${extensoes
      .map((e) => e.toUpperCase())
      .join(", ")}.`;

  const [arquivo, setArquivo] = useState(null);
  const [descricao, setDescricao] = useState("");
  const [erro, setErro] = useState(null);
  const inputRef = useRef(null);

  const limpar = () => {
    setArquivo(null);
    setDescricao("");
    if (inputRef.current) inputRef.current.value = "";
  };

  // Fail fast: recusa no momento da escolha, não depois do upload.
  const escolher = (evento) => {
    const escolhido = evento.target.files?.[0] || null;
    const problema = escolhido
      ? validarArquivoAnexo(escolhido, { maxBytes, extensoes })
      : null;
    setErro(problema);
    setArquivo(problema ? null : escolhido);
  };

  const enviar = async () => {
    const problema = validarArquivoAnexo(arquivo, { maxBytes, extensoes });
    if (problema) {
      setErro(problema);
      throw new Error(problema);
    }
    setErro(null);
    try {
      await onEnviar({ arquivo, descricao });
    } catch (err) {
      // `silentError` no botão: a mensagem fica aqui, junto do campo, em vez
      // de virar um segundo toast por cima do que o `request` já disparou.
      setErro(err?.message || "Não foi possível enviar o arquivo.");
      throw err;
    }
    limpar();
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        paddingTop: 8,
        borderTop: "1px solid var(--divider)",
      }}
    >
      <Field>
        {rotulo}
        <Input ref={inputRef} type="file" accept={accept} onChange={escolher} />
        {arquivo && (
          <InlineHint>
            {arquivo.name} ({formatarTamanho(arquivo.size)})
          </InlineHint>
        )}
      </Field>
      <Field>
        Descrição (opcional)
        <Input
          type="text"
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
          // Normalização no onBlur (ADR-0059): no onChange ela reescreveria o
          // texto tecla por tecla.
          onBlur={(e) => setDescricao(e.target.value.trim())}
          placeholder={placeholderDescricao}
          maxLength={200}
        />
      </Field>
      {erro && (
        <ErrorMsg>
          <FiAlertCircle size={12} aria-hidden="true" /> {erro}
        </ErrorMsg>
      )}
      <AsyncButton
        variant="secondary"
        onClick={enviar}
        successMessage={successMessage}
        silentError
        disabled={!arquivo}
        style={{ alignSelf: "flex-start" }}
      >
        <FiUpload size={13} aria-hidden="true" /> Anexar
      </AsyncButton>
      <InlineHint>{dicaDosLimites}</InlineHint>
    </div>
  );
}
